/**
 * @file Component Registry for JSON Editor
 * @version 1.1.0
 *
 * Centralized registry for editor components, allowing dynamic registration
 * and resolution of editors by type and variant.
 */

/**
 * Registry map: { [type]: { [variant]: Component } }
 * @type {Object<string, Object<string, object>>}
 */
const COMPONENT_MAP = {};

/**
 * Register an editor component for a type/variant combination.
 * Calling with an existing type/variant will override the previous registration.
 *
 * @param {string} type - Schema type ('primitive', 'object', 'array')
 * @param {string} variant - Display variant ('default', 'grid', 'table', 'inline', etc.)
 * @param {object} component - Vue component definition
 */
export function registerEditor(type, variant, component) {
  if (!COMPONENT_MAP[type]) {
    COMPONENT_MAP[type] = {};
  }
  COMPONENT_MAP[type][variant] = component;
}

/**
 * Resolve an editor component from a schema.
 *
 * Resolution order:
 * 1. If schema.editor is defined, return it directly
 * 2. Determine type from schema (object/array/primitive)
 * 3. Use schema.variant, or defaults.typeVariants[type], or 'default'
 * 4. Resolve from registry
 *
 * @param {object} schema - JSON Schema definition
 * @param {object} [defaults={}] - Defaults object, may contain typeVariants: { array: 'table', object: 'inline' }
 * @returns {object} Vue component definition
 * @throws {Error} If no editor is registered for the type or variant
 */
export function resolveEditor(schema, defaults = {}) {
  // Direct editor specified in schema
  if (schema.editor) {
    return schema.editor;
  }

  // Determine type from schema
  let type = 'primitive';
  if (schema.type === 'object' && schema.properties) {
    type = 'object';
  } else if (schema.type === 'array' && schema.items) {
    type = 'array';
  }

  // Get variant: schema.variant > defaults.typeVariants[type] > 'default'
  const typeVariants = defaults.typeVariants ?? {};
  const variant = schema.variant ?? typeVariants[type] ?? 'default';

  // Resolve from registry
  const typeMap = COMPONENT_MAP[type];
  if (!typeMap) {
    throw new Error(`No editors registered for type: ${type}`);
  }
  const component = typeMap[variant] ?? typeMap['default'];
  if (!component) {
    throw new Error(`No editor found for type: ${type}, variant: ${variant}`);
  }
  return component;
}

/**
 * Get all registered editors (for debugging/introspection).
 *
 * @returns {Object<string, Object<string, object>>} Copy of the registry
 */
export function getRegisteredEditors() {
  return { ...COMPONENT_MAP };
}
